import { ComboboxOption as HeadlessComboboxOption } from '@headlessui/react';
import { Icon } from '@src/components/Icon';
import { IconType } from '@src/IconType';
import { type ReactElement } from 'react';
import { classNames } from '@src/utils/classNames';
import { highlightNeedlesInHayStack } from '@src/utils/highlightNeedlesInHayStack';

type ComboboxOptionProperties<T> = {
  readonly option: T;
  readonly disabled: boolean;
  readonly hideCheckmark: boolean;
  readonly displayValue: (value: T) => string;
  readonly matchedText: string[];
  readonly searchQuery: string;
  readonly className?: string;
  readonly icon?: IconType;
  readonly additionalContent?: ReactElement;
};

export function ComboboxOption<T>({
  option,
  disabled,
  hideCheckmark,
  displayValue,
  matchedText,
  icon,
  searchQuery,
  className,
  additionalContent,
}: ComboboxOptionProperties<T>) {
  return (
    <HeadlessComboboxOption
      value={option}
      className={({ focus, selected }) =>
        classNames(
          'relative cursor-pointer select-none rounded-md py-3 px-4',
          (focus || selected) && 'bg-gray-100',
          className,
        )
      }
      disabled={disabled}
      data-testid={`option-${displayValue(option).replace(/\s/g, '-')}`}
    >
      {({ selected }) => (
        <span className="flex gap-1">
          {icon && (
            <Icon
              type={icon}
              className="h-6 w-6 flex-shrink-0"
              aria-hidden="true"
            />
          )}
          {highlightNeedlesInHayStack(
            searchQuery,
            matchedText,
            !hideCheckmark && selected,
          )}
          {additionalContent}
          {!hideCheckmark && selected && (
            <span
              className={classNames(
                'absolute inset-y-0 right-0 flex items-center pr-4',
              )}
            >
              <Icon type={IconType.Check} className="h-5 w-5" />
            </span>
          )}
        </span>
      )}
    </HeadlessComboboxOption>
  );
}
